import App from "./App.tsx";
import { createTheme, MantineProvider } from "@mantine/core";
import { ModalsProvider } from "@mantine/modals";
import { useEffect, useState } from "react";

import { type AppAbility, defineAbilitiesFor } from "./utils/abilities.ts";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import "@mantine/core/styles.css";
import "@mantine/spotlight/styles.css";
import "@mantine/tiptap/styles.css";
import "@mantine/notifications/styles.css";
import { useAuthStore } from "@utils/stores/authStore.ts";

type Env = "development" | "staging" | "production";

const environment = "staging" as Env;

console.log("Environment:", environment);

const theme = createTheme({
  colors: {
    blue: [
      "#E7F5FF",
      "#D0EBFF",
      "#A5D8FF",
      "#74C0FC",
      "#4DABF7",
      "#339AF0",
      "#0077CC", // Custom blue color
      "#1C7ED6",
      "#1971C2",
      "#1864AB",
    ],
    orange: [
      "#FFF9F0",
      "#FFEFD9",
      "#FFE4C2",
      "#FFD6A0",
      "#FFC97E",
      "#FFBC5C",
      "#FFB037", // Brand orange color
      "#E69A2E",
      "#CC8529",
      "#B37024",
    ],
  },
  components: {
    DateInput: {
      defaultProps: {
        popoverProps: { withinPortal: true },
      },
    },
    Modal: {
      defaultProps: {
        centered: true,
      },
    },
    Button: {
      defaultProps: {
        radius: "md",
      },
    },
  },
});

// Create a client
const queryClient = new QueryClient();

const Main = () => {
  const { role } = useAuthStore();
  const [ability, setAbility] = useState<AppAbility | null>(null);

  useEffect(() => {
    // Since we're no longer persisting, always set ability based on current role
    setAbility(defineAbilitiesFor(role));
  }, [role]);

  // Show loading until ability is initialized
  if (!ability) {
    return (
      <MantineProvider theme={theme} forceColorScheme="light">
        <ModalsProvider>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100vh",
              fontSize: "16px",
              color: "#666",
            }}
          >
            Loading...
          </div>
        </ModalsProvider>
      </MantineProvider>
    );
  }

  return (
    <MantineProvider theme={theme} forceColorScheme="light">
      <ModalsProvider>
        <HelmetProvider>
          <QueryClientProvider client={queryClient}>
            <App />
          </QueryClientProvider>
        </HelmetProvider>
      </ModalsProvider>
    </MantineProvider>
  );
};

export default Main;
